import {
	Injectable,
	signal,
	WritableSignal,
	afterNextRender,
	Injector,
	effect,
} from '@angular/core';
import { LocalStorageManagerService } from './data-managers/local-storage-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { CmsUtilsManagerService } from './data-managers/cms-utils-manager.service';
import { ILanguage } from '@models/cms/language';

@Injectable({
	providedIn: 'root',
})
export class TranslationService {
	$languages: string[] = [];
	languages: WritableSignal<string[]> = signal<string[]>([]);

	currentLanguage: WritableSignal<string> = signal<string>('pl');

	constructor(
		private storage: LocalStorageManagerService,
		private translate: TranslateService,
		private utilsManager: CmsUtilsManagerService,
		private injector: Injector,
	) {
		// Delay initialization to avoid circular dependency with TranslateService
		afterNextRender(
			() => {
				this._init();
			},
			{ injector: this.injector },
		);
	}

	private _init(): void {
		const languages = this.utilsManager.languages();
		this._prepareLanguages(languages);
		this._initLanguage();

		effect(
			() => {
				const updatedLanguages = this.utilsManager.languages();

				if (updatedLanguages.length !== this.$languages.length) {
					this._prepareLanguages(updatedLanguages);
					this._initLanguage();
				}
			},
			{ injector: this.injector },
		);
	}

	private _initLanguage(): void {
		this.currentLanguage.set(this.storage.currentLanguage);

		if (this.$languages.length > 0) {
			this.translate.addLangs(this.$languages);
			this.translate.setDefaultLang(this.storage.currentLanguage);
			this.translate.use(this.storage.currentLanguage);
		}
	}

	set language(language: string) {
		this.storage.currentLanguage = language;
		this.currentLanguage.set(language);
		this.translate.use(language).subscribe();
	}

	reloadLang(): void {
		this.translate.use(this.storage.currentLanguage);
	}

	getTranslation(key: string): string {
		return this.translate.instant(key);
	}

	private _prepareLanguages(languages: ILanguage[]): void {
		this.$languages = [];

		for (const lang of languages) {
			this.$languages.push(lang.short_code);
		}

		this.languages.set(this.$languages);
	}
}
